import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Sparkles, User, MessageSquare, Copy, Check, Highlighter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../componets/Toast';
import API_URL from '../config/API_URL';

const FloatingBriefingChat = ({ briefing, onClose, contacts }) => {
  const [selectedContact, setSelectedContact] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [highlightedText, setHighlightedText] = useState('');
  const [showHighlight, setShowHighlight] = useState(false);
  const messagesEndRef = useRef(null);
  const { showSuccess, showError } = useToast();

  const styles = {
    container: {
      position: 'fixed',
      right: '2rem',
      top: '2rem',
      bottom: '2rem',
      width: '420px',
      maxWidth: 'calc(100vw - 4rem)',
      background: '#ffffff',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      border: '2px solid #667eea',
    },
    header: {
      padding: '1.25rem',
      borderBottom: '2px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '18px 18px 0 0',
      color: 'white',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flex: 1,
    },
    headerTitle: {
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      fontSize: '1.1rem',
    },
    headerSubtitle: {
      fontSize: '0.8rem',
      opacity: 0.9,
      marginTop: '0.15rem',
    },
    closeButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      padding: '0.5rem',
      cursor: 'pointer',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 200ms',
    },
    contactSelector: {
      padding: '1rem',
      borderBottom: '1px solid #e2e8f0',
      background: '#f8fafc',
    },
    contactLabel: {
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#475569',
      marginBottom: '0.5rem',
      display: 'block',
    },
    contactSelect: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#ffffff',
      cursor: 'pointer',
      transition: 'all 200ms',
      outline: 'none',
    },
    highlightedTextBanner: {
      padding: '0.75rem 1rem',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '1px solid #fbbf24',
      borderRadius: '0',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      position: 'relative',
      animation: 'slideDown 0.3s ease-out',
    },
    highlightIcon: {
      color: '#f59e0b',
      flexShrink: 0,
      marginTop: '0.1rem',
    },
    highlightContent: {
      flex: 1,
    },
    highlightLabel: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#92400e',
      marginBottom: '0.35rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    highlightText: {
      fontSize: '0.85rem',
      color: '#78350f',
      lineHeight: 1.5,
      maxHeight: '60px',
      overflowY: 'auto',
      fontStyle: 'italic',
    },
    removeHighlightButton: {
      background: 'rgba(146, 64, 14, 0.1)',
      border: 'none',
      borderRadius: '6px',
      padding: '0.35rem',
      cursor: 'pointer',
      color: '#92400e',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 200ms',
      position: 'absolute',
      top: '0.75rem',
      right: '0.75rem',
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    message: {
      padding: '1rem',
      borderRadius: '12px',
      fontSize: '0.9rem',
      lineHeight: 1.6,
      position: 'relative',
    },
    userMessage: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      alignSelf: 'flex-end',
      maxWidth: '85%',
      marginLeft: 'auto',
    },
    aiMessage: {
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      alignSelf: 'flex-start',
      maxWidth: '90%',
    },
    messageLabel: {
      fontSize: '0.7rem',
      fontWeight: 600,
      color: '#64748b',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    messageText: {
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
      color: '#0f172a',
    },
    copyButton: {
      position: 'absolute',
      top: '0.5rem',
      right: '0.5rem',
      background: '#667eea',
      border: 'none',
      borderRadius: '6px',
      padding: '0.4rem 0.75rem',
      cursor: 'pointer',
      color: 'white',
      fontSize: '0.75rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
    },
    inputContainer: {
      padding: '1rem',
      borderTop: '2px solid #e2e8f0',
      background: '#ffffff',
      borderRadius: '0 0 18px 18px',
    },
    inputWrapper: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-end',
    },
    textarea: {
      flex: 1,
      padding: '0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      resize: 'none',
      minHeight: '80px',
      maxHeight: '150px',
      transition: 'all 200ms',
    },
    sendButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0.875rem 1rem',
      cursor: 'pointer',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 200ms',
      minWidth: '48px',
      height: '48px',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      color: '#64748b',
      textAlign: 'center',
      height: '100%',
    },
    emptyIcon: {
      marginBottom: '1rem',
      opacity: 0.5,
    },
    emptyTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      marginBottom: '0.5rem',
      color: '#475569',
    },
    emptyText: {
      fontSize: '0.9rem',
      lineHeight: 1.5,
    },
    briefingContext: {
      background: '#fef3c7',
      border: '1px solid #fbbf24',
      borderRadius: '8px',
      padding: '0.75rem',
      fontSize: '0.85rem',
      color: '#92400e',
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.5rem',
    },
    loadingDots: {
      display: 'flex',
      gap: '0.35rem',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
    },
    loadingDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#667eea',
    },
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for text selection on the page
  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();
      
      if (text && text.length > 0) {
        setHighlightedText(text);
        setShowHighlight(true);
      }
    };

    // Add listener for mouseup (when user finishes selecting)
    document.addEventListener('mouseup', handleSelection);
    
    return () => {
      document.removeEventListener('mouseup', handleSelection);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGenerate = async () => {
    if (!selectedContact) {
      showError('Please select a contact first');
      return;
    }

    if (!inputValue.trim() && !highlightedText) {
      showError('Please enter your message context or highlight text from the briefing');
      return;
    }

    // Combine user input with highlighted text
    let fullContext = inputValue.trim();
    if (highlightedText) {
      fullContext = `${fullContext}\n\nHighlighted from briefing: "${highlightedText}"`;
    }

    const userMessage = fullContext;
    setInputValue('');
    setHighlightedText(''); // Clear highlighted text after using it
    setShowHighlight(false);
    setIsGenerating(true);

    // Add user message to chat
    const updatedMessages = [
      ...messages,
      { role: 'user', content: userMessage }
    ];
    setMessages(updatedMessages);

    try {
      const response = await fetch(`${API_URL}/api/generate-briefing-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          contact_id: parseInt(selectedContact),
          briefing_id: briefing.id,
          user_context: userMessage,
          conversation_history: updatedMessages
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessages([
          ...updatedMessages,
          { role: 'assistant', content: data.message }
        ]);
        showSuccess('Message generated!');
      } else {
        showError(data.error || 'Failed to generate message');
        setMessages(updatedMessages);
      }
    } catch (error) {
      console.error('Generation error:', error);
      showError('Failed to generate message');
      setMessages(updatedMessages);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    showSuccess('Copied to clipboard!');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleRemoveHighlight = () => {
    setHighlightedText('');
    setShowHighlight(false);
  };

  return (
    <motion.div
      initial={{ x: 500, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 500, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      style={styles.container}
    >
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <MessageSquare size={24} />
          <div>
            <div style={styles.headerTitle}>Generate Message</div>
            <div style={styles.headerSubtitle}>Based on briefing</div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={styles.closeButton}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Contact Selector */}
      <div style={styles.contactSelector}>
        <label style={styles.contactLabel}>
          <User size={14} style={{ display: 'inline', marginRight: '0.35rem' }} />
          Select Contact *
        </label>
        <select
          value={selectedContact}
          onChange={(e) => setSelectedContact(e.target.value)}
          style={styles.contactSelect}
          onFocus={(e) => {
            e.target.style.borderColor = '#667eea';
            e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = 'none';
          }}
        >
          <option value="">Choose a contact...</option>
          {contacts.map((contact) => (
            <option key={contact.id} value={contact.id}>
              {contact.name} {contact.company ? `- ${contact.company}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Highlighted Text Banner */}
      <AnimatePresence>
        {showHighlight && highlightedText && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={styles.highlightedTextBanner}>
              <Highlighter size={18} style={styles.highlightIcon} />
              <div style={styles.highlightContent}>
                <div style={styles.highlightLabel}>
                  ✨ Text Highlighted
                </div>
                <div style={styles.highlightText}>
                  "{highlightedText}"
                </div>
              </div>
              <button
                onClick={handleRemoveHighlight}
                style={styles.removeHighlightButton}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(146, 64, 14, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(146, 64, 14, 0.1)';
                }}
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages Container */}
      <div style={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div style={styles.emptyState}>
            <Sparkles size={48} style={styles.emptyIcon} />
            <div style={styles.emptyTitle}>Start Generating</div>
            <div style={styles.emptyText}>
              Select a contact and tell me what type of message you'd like. 
              <br /><br />
              💡 <strong>Tip:</strong> Highlight text from the briefing and it will be automatically included!
            </div>
          </div>
        ) : (
          <>
            <div style={styles.briefingContext}>
              <Sparkles size={16} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
              <div>
                <strong>Briefing Context:</strong> {briefing.topic_name} - {briefing.summary.slice(0, 100)}...
              </div>
            </div>
            
            <AnimatePresence>
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    ...styles.message,
                    ...(msg.role === 'user' ? styles.userMessage : styles.aiMessage)
                  }}
                >
                  <div style={styles.messageLabel}>
                    {msg.role === 'user' ? 'You' : 'AI Generated'}
                  </div>
                  <div style={styles.messageText}>{msg.content}</div>
                  
                  {msg.role === 'assistant' && (
                    <button
                      onClick={() => handleCopy(msg.content, index)}
                      style={styles.copyButton}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.background = '#5568d3';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.background = '#667eea';
                      }}
                    >
                      {copiedIndex === index ? (
                        <>
                          <Check size={14} />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          Copy
                        </>
                      )}
                    </button>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {isGenerating && (
              <div style={styles.loadingDots}>
                <motion.div
                  style={styles.loadingDot}
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                />
                <motion.div
                  style={styles.loadingDot}
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                />
                <motion.div
                  style={styles.loadingDot}
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                />
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Container */}
      <div style={styles.inputContainer}>
        <div style={styles.inputWrapper}>
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={selectedContact 
              ? (highlightedText 
                  ? `Add instructions for the highlighted text...` 
                  : `e.g., "Write a casual email about this"`)
              : "Select a contact first..."}
            disabled={!selectedContact}
            style={{
              ...styles.textarea,
              opacity: selectedContact ? 1 : 0.6,
            }}
            onFocus={(e) => {
              if (selectedContact) {
                e.target.style.borderColor = '#667eea';
                e.target.style.background = '#ffffff';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.background = '#f8fafc';
              e.target.style.boxShadow = 'none';
            }}
          />
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !selectedContact || (!inputValue.trim() && !highlightedText)}
            style={{
              ...styles.sendButton,
              opacity: isGenerating || !selectedContact || (!inputValue.trim() && !highlightedText) ? 0.5 : 1,
              cursor: isGenerating || !selectedContact || (!inputValue.trim() && !highlightedText) ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!isGenerating && selectedContact && (inputValue.trim() || highlightedText)) {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {isGenerating ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles size={20} />
              </motion.div>
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default FloatingBriefingChat;